import React, {Dispatch, PropsWithChildren, ReactNode, RefObject, SetStateAction} from 'react';
import {isEmpty} from 'lodash';
import cn from 'classnames';

import Paginator from 'shared/ui/Paginator/Paginator';
import IPagination from 'shared/api/model/IPagination';
import IOrderBy from 'shared/model/IOrderBy';
import {RowFactory, tableRowsWithLoader} from 'shared/ui/Table/tableHelper';
import { isTranslate } from 'shared/lib/lang';

import TableHead from './SortableTableHead/TableHead';
import SortableTableHead from './SortableTableHead/SortableTableHead';

import styles from './Table.module.scss';

export type SetOrderByType = Dispatch<SetStateAction<IOrderBy | undefined>>;

export interface IColumn {
    label: string | ReactNode,
    sort?: string,
    key?: string,
}

export interface ITableProps<T extends object> {
    columns: IColumn[],
    items?: T[] | null,
    rowFactory: RowFactory<T>,
    isLoading: boolean,
    pagination?: IPagination<T>,
    className?: string,
    sort?: IOrderBy,
    setSort?: SetOrderByType,
    setPage?: Dispatch<SetStateAction<number>>,
    tableRef?: RefObject<HTMLTableElement>,
    emptyStateComponent?: ReactNode,
}

function Table<T extends object>(
    {
        columns,
        items,
        rowFactory,
        isLoading,
        pagination,
        className,
        sort,
        setSort,
        setPage,
        tableRef,
        emptyStateComponent,
    }: PropsWithChildren<ITableProps<T>>,
) {
    const sortableNum = columns.reduce(
        (accumulator, currentValue) => accumulator + (currentValue.sort ? 1 : 0), 0);

    const tableHead = (sortableNum > 0 && setSort)
        ? <SortableTableHead
            columns={columns}
            sort={sort}
            set={setSort}
        />
        : <TableHead columns={columns}/>;

    return (
        <div className={cn(
            styles.wrapper,
            pagination?.totalItems && pagination.totalItems > 50 && styles.wrapWithPagination,
        )}>
            <div className={styles.container}>
                <table
                    ref={tableRef}
                    className={cn(styles.content, 'table table-hover table-condensed', className)}>

                    {tableHead}

                    <tbody className={cn(
                        styles.tbody,
                        styles[isTranslate() ? 'enTBody' : 'ruTBody'],
                    )}>
                        {tableRowsWithLoader<T>(
                            items ?? [],
                            isLoading,
                            columns.length,
                            rowFactory,
                            emptyStateComponent,
                        )}
                    </tbody>

                    {!isEmpty(pagination) && (
                        <tfoot className={styles.tfoot}>
                            <tr>
                                <td colSpan={columns.length}>
                                    <Paginator
                                        initialPage={pagination.currentPage}
                                        lastPage={pagination.lastPage}
                                        setPage={setPage}
                                    />
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
}

export default React.memo(Table) as typeof Table;